import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from './Icon';
import Button from './Button';
import PaywallModal from './PaywallModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Profile = ({ token, user, onUserUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [credits, setCredits] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [activeTab, setActiveTab] = useState('account');
  const [enterpriseInfo, setEnterpriseInfo] = useState(null);
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    phone: '',
    first_name: '',
    last_name: '',
    bio: '',
    clinic_name: '',
    education: ''
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    if (user) {
      setProfileForm({
        phone: user.phone || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        bio: user.bio || '',
        clinic_name: user.clinic_name || '',
        education: user.education || ''
      });
      
      // Load credits and enterprise info for patients/clients
      if (user.role === 'patient' || user.role === 'client') {
        loadCredits();
        loadEnterpriseInfo();
      }
    }
  }, [user]);

  const loadEnterpriseInfo = async () => {
    try {
      const response = await fetch(`${API_BASE}/enterprise/my-company`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setEnterpriseInfo(data);
        // If user was on payment tab, redirect to company tab
        setActiveTab(prev => prev === 'payment' ? 'company' : prev);
      } else {
        setEnterpriseInfo(null);
      }
    } catch (err) {
      setEnterpriseInfo(null);
    }
  };

  const loadCredits = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/credits/balance`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const creditData = await response.json();
        setCredits(creditData.current_balance);
      } else {
        setCredits(0);
      }
    } catch (error) {
      console.error('Error loading credits:', error);
      setCredits(0);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Prepare the data for submission
      // Auto-generate full name from first_name and last_name
      const fullName = `${profileForm.first_name?.trim() || ''} ${profileForm.last_name?.trim() || ''}`.trim() || profileForm.first_name?.trim() || profileForm.last_name?.trim() || 'User';
      
      const submitData = {
        ...profileForm,
        name: fullName // Auto-generated from first_name + last_name
      };

      const response = await axios.put(
        `${API_BASE}/auth/profile`,
        submitData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccess('Profilen har uppdaterats!');
      
      // Update user in parent component
      if (onUserUpdate) {
        onUserUpdate(response.data);
      }
      
    } catch (err) {
      console.error('Profile update error:', err);
      const errorMsg = err.response?.data?.detail || 'Kunde inte uppdatera profilen';
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setError('De nya lösenorden stämmer inte överens');
      return;
    }

    if (passwordForm.new_password.length < 8) {
      setError('Det nya lösenordet måste vara minst 8 tecken långt');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await axios.put(
        `${API_BASE}/auth/change-password`,
        {
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccess('Lösenordet har ändrats!');
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setShowPasswordChange(false);
      
    } catch (err) {
      console.error('Password change error:', err);
      const errorMsg = err.response?.data?.detail || 'Kunde inte ändra lösenordet';
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <Icon name="loading" size={32} className="animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-600">Laddar konto...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-red-100 text-red-700 rounded-full flex items-center justify-center font-semibold text-xl">
              {user.name ? user.name.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2) : 'U'}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Mitt konto</h1>
              <p className="text-gray-600">
                {user.role === 'therapist' ? 'Terapeut' : 'Klient'} • {user.email}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('account')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
              activeTab === 'account'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <span>Kontoinformation</span>
            </div>
          </button>
          
          {(user.role === 'patient' || user.role === 'client') && !enterpriseInfo && (
            <button
              onClick={() => setActiveTab('payment')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'payment'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <span>Betalning</span>
              </div>
            </button>
          )}

          {enterpriseInfo && (
            <button
              onClick={() => setActiveTab('company')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'company'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <span>Mitt företag</span>
              </div>
            </button>
          )}
        </nav>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">⚠️ {error}</p>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-800">✅ {success}</p>
        </div>
      )}

      {/* Account Information Tab */}
      {activeTab === 'account' && (
        <div className="space-y-6">
          {/* Profile Information */}
          <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Kontoinformation</h3>
          
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Förnamn *
                </label>
                <input
                  type="text"
                  id="first_name"
                  value={profileForm.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Efternamn
                </label>
                <input
                  type="text"
                  id="last_name"
                  value={profileForm.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Telefonnummer
              </label>
              <input
                type="tel"
                id="phone"
                value={profileForm.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="+46 70 123 45 67"
              />
            </div>

            {/* Email (read-only) */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                E-postadress
              </label>
              <input
                type="email"
                id="email"
                value={user.email}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-50 text-gray-500"
                disabled
              />
              <p className="text-xs text-gray-500 mt-1">
                E-postadressen kan inte ändras av säkerhetsskäl.
              </p>
            </div>

            

            {/* Simplified therapist-specific fields */}
            {user.role === 'therapist' && (
              <>
              {/* Bio/Description */}
              <div>
                <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">
                  {user.role === 'therapist' ? 'Professionell beskrivning' : 'Om mig'}
                </label>
                <textarea
                  id="bio"
                  value={profileForm.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  rows={4}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={user.role === 'therapist' 
                    ? 'Berätta om din bakgrund, specialiseringar och behandlingsmetoder...'
                    : 'Berätta lite om dig själv om du vill...'
                  }
                />
              </div>
                <div>
                  <label htmlFor="clinic_name" className="block text-sm font-medium text-gray-700 mb-1">
                    Klinik/Arbetsplats
                  </label>
                  <input
                    type="text"
                    id="clinic_name"
                    value={profileForm.clinic_name}
                    onChange={(e) => handleInputChange('clinic_name', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Namn på din klinik eller arbetsplats"
                  />
                </div>

                <div>
                  <label htmlFor="education" className="block text-sm font-medium text-gray-700 mb-1">
                    Utbildning
                  </label>
                  <input
                    type="text"
                    id="education"
                    value={profileForm.education}
                    onChange={(e) => handleInputChange('education', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="t.ex. Psykolog, Legitimerad psykoterapeut"
                  />
                </div>
              </>
            )}

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={saving}
                className="px-6 py-2"
              >
                {saving ? (
                  <>
                    <Icon name="loading" size={16} className="animate-spin mr-2" />
                    Sparar...
                  </>
                ) : (
                  <>
                    <Icon name="save" size={16} className="mr-2" />
                    Spara ändringar
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Password Change Section */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Lösenord</h3>
            {!showPasswordChange && (
              <Button
                variant="outline"
                onClick={() => setShowPasswordChange(true)}
                size="sm"
              >
                <Icon name="lock-keyhole" size={16} className="mr-2" />
                Ändra lösenord
              </Button>
            )}
          </div>

          {showPasswordChange ? (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label htmlFor="current_password" className="block text-sm font-medium text-gray-700 mb-1">
                  Nuvarande lösenord *
                </label>
                <input
                  type="password"
                  id="current_password"
                  value={passwordForm.current_password}
                  onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-1">
                  Nytt lösenord *
                </label>
                <input
                  type="password"
                  id="new_password"
                  value={passwordForm.new_password}
                  onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  minLength="8"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minst 8 tecken långt
                </p>
              </div>

              <div>
                <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-1">
                  Bekräfta nytt lösenord *
                </label>
                <input
                  type="password"
                  id="confirm_password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => handlePasswordChange('confirm_password', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setPasswordForm({
                      current_password: '',
                      new_password: '',
                      confirm_password: ''
                    });
                    setError('');
                  }}
                >
                  Avbryt
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  variant="primary"
                >
                  {saving ? (
                    <>
                      <Icon name="loading" size={16} className="animate-spin mr-2" />
                      Ändrar...
                    </>
                  ) : (
                    <>
                      <Icon name="key" size={16} className="mr-2" />
                      Ändra lösenord
                    </>
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <p className="text-gray-600 text-sm">
              Klicka på "Ändra lösenord" för att uppdatera ditt lösenord.
            </p>
          )}
        </div>
      </div>
        </div>
      )}

      {/* Payment & Credits Tab - Only for Patients/Clients without enterprise */}
      {activeTab === 'payment' && (user.role === 'patient' || user.role === 'client') && !enterpriseInfo && (
        <div className="space-y-6">
          {/* Credits Section */}
          <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-6">Credits & Betalning</h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <Icon name="gem" size={16} className="text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-medium text-gray-900">
                    Dina Credits
                  </h4>
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl font-bold text-blue-600">
                      {credits !== null ? credits : '...'}
                    </span>
                    <span className="text-sm text-gray-500">credits kvar</span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <Button
                  onClick={() => setShowPaywall(true)}
                  variant="primary"
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-md hover:shadow-lg"
                >
                  Köp Credits
                </Button>
              </div>
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start space-x-2">
                <Icon name="info" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Engångsbetalning</p>
                  <p>Vi erbjuder endast engångsköp av credits - inga prenumerationer eller återkommande avgifter. Du betalar bara för det du använder.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Company Tab */}
      {activeTab === 'company' && enterpriseInfo && (
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-6">Mitt företag</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center font-bold text-white text-lg">
                    {enterpriseInfo.organization_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-lg">{enterpriseInfo.organization_name}</p>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                      Aktiv
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Avdelning</p>
                    <p className="text-sm font-medium text-gray-900">
                      {enterpriseInfo.department || '—'}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Anslöt</p>
                    <p className="text-sm font-medium text-gray-900">
                      {enterpriseInfo.invite_accepted_at
                        ? new Date(enterpriseInfo.invite_accepted_at).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })
                        : '—'}
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Vad innebär detta?</p>
                  <p className="text-sm text-gray-700">
                    Ditt konto är kopplat till {enterpriseInfo.organization_name} som betalar för din tillgång till tjänsten.
                    Du kan använda alla funktioner utan att behöva köpa credits.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Paywall Modal */}
      {showPaywall && (
        <PaywallModal 
          isOpen={showPaywall}
          onClose={() => {
            setShowPaywall(false);
            // Reload credits after closing modal (in case user made a purchase)
            if (user.role === 'patient' || user.role === 'client') {
              loadCredits();
            }
          }}
          credits={credits}
        />
      )}
    </div>
  );
};

export default Profile;
